/** Stock & Inventory — Phase 2 tabs (reservations, transfers, returns,
 *  customer-owned, demo/loaner, repair) + Phase 3 tabs (aging, reorder,
 *  daily reports) + Morning Stock Brief. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field, ProductPicker } from "./StockInventoryDialogs";

export function ReceiveCustomerStockDialog({ meta, onClose, onDone }) {
  const [customers, setCustomers] = useState([]);
  const [expected, setExpected] = useState([]);
  const [product, setProduct] = useState(null);
  const [f, setF] = useState({ customer_id: "", customer_name: "", quantity: 1,
    serialText: "", warehouse_id: "", condition: "GOOD", source_supplier: "",
    customer_po: "", customer_purchase_ref: "", project: "", expected_id: "",
    inspection_pending: false });
  useEffect(() => {
    api.get("/inventory/customers", { params: { q: "" } }).then(({ data }) => setCustomers(data.items || []));
    api.get("/inventory/customer-stock/expected").then(({ data }) =>
      setExpected((data.items || []).filter((x) => x.status === "EXPECTED")));
  }, []);
  const submit = async () => {
    if (!f.customer_id || !product || !f.warehouse_id) {
      toast.error("Customer, product and warehouse required"); return; }
    try {
      const { data } = await api.post("/inventory/customer-stock/receive", {
        ...f, product_id: product.id,
        quantity: Number(f.quantity) || 1,
        serials: f.serialText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
        expected_id: f.expected_id || null });
      toast.success(`${data.receipt.code} — ${data.match_status.replace(/_/g, " ")}`);
      if (data.apple_notice) toast.warning(data.apple_notice, { duration: 8000 });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Receipt failed"); }
  };
  const inputCls2 = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
  return (
    <Modal title="+ Receive Customer Stock (CSR)" onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="csr-dialog">
        <Field label="Customer" required>
          <select value={f.customer_id} data-testid="csr-customer"
              onChange={(e) => { const c = customers.find((x) => x.id === e.target.value);
                setF({ ...f, customer_id: e.target.value, customer_name: c?.name || "" }); }}
              className={inputCls2}>
            <option value="">— pick —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>
        <div className="col-span-2"><Field label="Product" required>
          <ProductPicker value={product} onPick={setProduct} testid="csr-product" /></Field></div>
        <Field label="Warehouse" required>
          <select value={f.warehouse_id} data-testid="csr-warehouse"
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })} className={inputCls2}>
            <option value="">—</option>
            {(meta?.warehouses || []).map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
          </select></Field>
        <Field label="Quantity"><input type="number" min={1} value={f.quantity}
            onChange={(e) => setF({ ...f, quantity: e.target.value })}
            data-testid="csr-qty" className={inputCls2} /></Field>
        <Field label="Condition">
          <select value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })}
              data-testid="csr-condition" className={inputCls2}>
            {(meta?.config?.conditions || []).map((c) => <option key={c}>{c}</option>)}
          </select></Field>
        <Field label="Source supplier"><input value={f.source_supplier}
            onChange={(e) => setF({ ...f, source_supplier: e.target.value })}
            data-testid="csr-supplier" className={inputCls2} /></Field>
        <Field label="Customer PO"><input value={f.customer_po}
            onChange={(e) => setF({ ...f, customer_po: e.target.value })} className={inputCls2} /></Field>
        <Field label="Purchase reference"><input value={f.customer_purchase_ref}
            onChange={(e) => setF({ ...f, customer_purchase_ref: e.target.value })} className={inputCls2} /></Field>
        <Field label="Match expected pre-alert">
          <select value={f.expected_id} data-testid="csr-expected"
              onChange={(e) => setF({ ...f, expected_id: e.target.value })} className={inputCls2}>
            <option value="">— none / direct —</option>
            {expected.filter((x) => !f.customer_id || x.customer_id === f.customer_id).map((x) =>
              <option key={x.id} value={x.id}>{x.code} · {x.product_name} × {x.quantity}</option>)}
          </select></Field>
        <label className="text-[10px] text-slate-600 flex items-center gap-1.5 col-span-2 pt-4">
          <input type="checkbox" checked={f.inspection_pending}
              onChange={(e) => setF({ ...f, inspection_pending: e.target.checked })}
              data-testid="csr-inspect" /> Hold for inspection before making available
        </label>
        <div className="col-span-3"><Field label="Serials (one per line — required for serial-tracked)">
          <textarea rows={2} value={f.serialText} data-testid="csr-serials"
              onChange={(e) => setF({ ...f, serialText: e.target.value })}
              className={inputCls2 + " font-mono"} /></Field></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="csr-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-800 text-white">Confirm Receipt</button>
      </div>
    </Modal>
  );
}

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const serialsFromText = (t) => t.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

const StageBadge = ({ s }) => {
  const cls = { RESERVED: "bg-blue-100 text-blue-800", PICKED: "bg-indigo-100 text-indigo-800",
    PACKED: "bg-purple-100 text-purple-800", DISPATCHED: "bg-amber-100 text-amber-800",
    IN_TRANSIT: "bg-orange-100 text-orange-800", DELIVERED: "bg-emerald-100 text-emerald-800",
    RECEIVED: "bg-emerald-100 text-emerald-800", REQUESTED: "bg-slate-100 text-slate-700",
    CANCELLED: "bg-slate-200 text-slate-500" }[s] || "bg-slate-100 text-slate-600";
  return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{s}</span>;
};

// ---------------------------------------------------------------------------
export function ReservationsTab({ meta, canWrite }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/inventory/reservations").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const NEXT = { RESERVED: "PICKED", PICKED: "PACKED", PACKED: "DISPATCHED",
    DISPATCHED: "IN_TRANSIT", IN_TRANSIT: "DELIVERED" };
  const advance = async (r) => {
    try {
      await api.post(`/inventory/reservations/${r.id}/advance`, { to_stage: NEXT[r.stage] });
      toast.success(`${r.code} → ${NEXT[r.stage]}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async (r) => {
    if (!window.confirm(`Cancel ${r.code} and release the stock?`)) return;
    try { await api.post(`/inventory/reservations/${r.id}/cancel`); toast.success("Released"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="inv-reservations">
      {canWrite && (
        <button onClick={() => setOpen(true)} data-testid="rsv-new"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-blue-800 text-white">+ Allocate Stock</button>
      )}
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Reservation</th>
            <th className="text-left px-3 py-2">Order</th>
            <th className="text-left px-3 py-2">Product</th>
            <th className="text-left px-3 py-2">Qty / Serials</th>
            <th className="text-left px-3 py-2">Stage</th>
            <th className="text-right px-3 py-2 w-44"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">No reservations — allocate stock against an order.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`rsv-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{r.code}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.order_ref}</td>
                <td className="px-3 py-2">{r.product_name}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.quantity} · {(r.serials || []).join(", ") || "qty"}</td>
                <td className="px-3 py-2"><StageBadge s={r.stage} /></td>
                <td className="px-3 py-2 text-right space-x-1">
                  {canWrite && NEXT[r.stage] && (
                    <button onClick={() => advance(r)} data-testid={`rsv-advance-${r.code}`}
                        className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">→ {NEXT[r.stage]}</button>)}
                  {canWrite && ["RESERVED", "PICKED", "PACKED"].includes(r.stage) && (
                    <button onClick={() => cancel(r)} className="text-[10px] px-2 py-1 rounded border border-rose-400 text-rose-700">Cancel</button>)}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {open && <AllocateDialog meta={meta} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function AllocateDialog({ meta, onClose, onDone }) {
  const [product, setProduct] = useState(null);
  const [f, setF] = useState({ order_ref: "", quantity: 1, warehouse_id: "", serialText: "", customer_name: "" });
  const submit = async () => {
    if (!product || !f.order_ref || !f.warehouse_id) { toast.error("Product, order ref and warehouse required"); return; }
    try {
      const { data } = await api.post("/inventory/reservations", {
        order_ref: f.order_ref, product_id: product.id, quantity: Number(f.quantity) || 1,
        serials: serialsFromText(f.serialText), warehouse_id: f.warehouse_id,
        customer_name: f.customer_name || null });
      toast.success(`Allocated — ${data.reservation.code}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Allocation failed"); }
  };
  return (
    <Modal title="Allocate Stock to Order" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="allocate-dialog">
        <div className="col-span-2"><Field label="Product" required>
          <ProductPicker value={product} onPick={setProduct} testid="rsv-product" /></Field></div>
        <Field label="Order reference" required>
          <input value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })}
              data-testid="rsv-order" className={inputCls} /></Field>
        <Field label="Warehouse" required>
          <select value={f.warehouse_id} onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
              data-testid="rsv-warehouse" className={inputCls}>
            <option value="">— pick —</option>
            {(meta?.warehouses || []).map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
          </select></Field>
        <Field label="Quantity"><input type="number" min={1} value={f.quantity}
            onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="rsv-qty" className={inputCls} /></Field>
        <Field label="Customer"><input value={f.customer_name}
            onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={inputCls} /></Field>
        {(product?.tracking_method || "SERIAL") === "SERIAL" && (
          <div className="col-span-2"><Field label="Serials to reserve (one per line)">
            <textarea rows={2} value={f.serialText} data-testid="rsv-serials"
                onChange={(e) => setF({ ...f, serialText: e.target.value })}
                className={inputCls + " font-mono"} /></Field></div>)}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="rsv-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-blue-800 text-white">Allocate</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
export function TransfersTab({ meta, canWrite }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/inventory/transfers").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const NEXT = { REQUESTED: "PICKED", PICKED: "IN_TRANSIT", IN_TRANSIT: "RECEIVED" };
  const whName = (id) => (meta?.warehouses || []).find((w) => w.id === id)?.code || id;
  const advance = async (t) => {
    try { await api.post(`/inventory/transfers/${t.id}/advance`, { to_stage: NEXT[t.stage] });
      toast.success(`${t.code} → ${NEXT[t.stage]}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="inv-transfers">
      {canWrite && <button onClick={() => setOpen(true)} data-testid="trf-new"
          className="text-[11px] px-2.5 py-1.5 rounded-md bg-indigo-800 text-white">+ New Transfer</button>}
      <div className="border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Transfer</th><th className="text-left px-3 py-2">Product</th>
            <th className="text-left px-3 py-2">Route</th><th className="text-left px-3 py-2">Qty</th>
            <th className="text-left px-3 py-2">Stage</th><th className="text-right px-3 py-2 w-32"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">No transfers.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`trf-row-${t.code}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{t.code}</td>
                <td className="px-3 py-2">{t.product_name}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{whName(t.from_warehouse_id)} → {whName(t.to_warehouse_id)}</td>
                <td className="px-3 py-2 font-mono">{t.quantity}</td>
                <td className="px-3 py-2"><StageBadge s={t.stage} /></td>
                <td className="px-3 py-2 text-right">
                  {canWrite && NEXT[t.stage] && (
                    <button onClick={() => advance(t)} data-testid={`trf-advance-${t.code}`}
                        className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">→ {NEXT[t.stage]}</button>)}
                </td></tr>))}
          </tbody>
        </table>
      </div>
      {open && <TransferDialog meta={meta} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function TransferDialog({ meta, onClose, onDone }) {
  const [product, setProduct] = useState(null);
  const [f, setF] = useState({ quantity: 1, from_warehouse_id: "", to_warehouse_id: "", serialText: "" });
  const submit = async () => {
    if (!product || !f.from_warehouse_id || !f.to_warehouse_id) { toast.error("Fill all fields"); return; }
    try {
      const { data } = await api.post("/inventory/transfers", {
        product_id: product.id, quantity: Number(f.quantity) || 1,
        serials: serialsFromText(f.serialText),
        from_warehouse_id: f.from_warehouse_id, to_warehouse_id: f.to_warehouse_id });
      toast.success(`Transfer ${data.transfer.code} requested`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };
  const whOpts = (meta?.warehouses || []);
  return (
    <Modal title="Warehouse Transfer" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="transfer-dialog">
        <div className="col-span-2"><Field label="Product" required>
          <ProductPicker value={product} onPick={setProduct} testid="trf-product" /></Field></div>
        <Field label="From warehouse" required>
          <select value={f.from_warehouse_id} onChange={(e) => setF({ ...f, from_warehouse_id: e.target.value })}
              data-testid="trf-from" className={inputCls}>
            <option value="">—</option>{whOpts.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
          </select></Field>
        <Field label="To warehouse" required>
          <select value={f.to_warehouse_id} onChange={(e) => setF({ ...f, to_warehouse_id: e.target.value })}
              data-testid="trf-to" className={inputCls}>
            <option value="">—</option>{whOpts.map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
          </select></Field>
        <Field label="Quantity"><input type="number" min={1} value={f.quantity}
            onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="trf-qty" className={inputCls} /></Field>
        {(product?.tracking_method || "SERIAL") === "SERIAL" && (
          <div className="col-span-2"><Field label="Serials (one per line)">
            <textarea rows={2} value={f.serialText} data-testid="trf-serials"
                onChange={(e) => setF({ ...f, serialText: e.target.value })}
                className={inputCls + " font-mono"} /></Field></div>)}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="trf-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-indigo-800 text-white">Request Transfer</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
export function ReturnsTab({ meta, canWrite }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/inventory/returns").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="inv-returns">
      {canWrite && <button onClick={() => setOpen(true)} data-testid="ret-new"
          className="text-[11px] px-2.5 py-1.5 rounded-md bg-amber-700 text-white">+ Receive Return</button>}
      <div className="border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Return</th><th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Triage</th>
            <th className="text-left px-3 py-2">Source</th><th className="text-left px-3 py-2">When</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">No returns received.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ret-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{r.code}</td>
                <td className="px-3 py-2 text-[10px]">{r.return_type.replace(/_/g, " ")}</td>
                <td className="px-3 py-2">{r.product_name}</td>
                <td className="px-3 py-2 text-[10px]">{(r.triage || []).map((t, i) =>
                  <div key={i} className="font-mono">{t.serial || `qty ${t.quantity}`} → {t.condition} ({t.status})</div>)}</td>
                <td className="px-3 py-2 text-[10px]">{r.source || r.customer_name || "—"}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{fmtD(r.received_at)}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {open && <ReturnDialog meta={meta} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ReturnDialog({ meta, onClose, onDone }) {
  const [product, setProduct] = useState(null);
  const [f, setF] = useState({ return_type: "CUSTOMER_RETURN", warehouse_id: "",
    customer_name: "", ownership: "SUNTRONIX_OWNED" });
  const [lines, setLines] = useState([{ serial: "", quantity: 1, condition: "GOOD" }]);
  const patch = (i, u) => setLines((ls) => ls.map((l, j) => j === i ? { ...l, ...u } : l));
  const submit = async () => {
    if (!product || !f.warehouse_id) { toast.error("Product and warehouse required"); return; }
    try {
      const { data } = await api.post("/inventory/returns", {
        ...f, product_id: product.id,
        lines: lines.map((l) => ({ serial: l.serial || null, quantity: Number(l.quantity) || 1, condition: l.condition })) });
      toast.success(`Return ${data.return.code} triaged`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Return failed"); }
  };
  return (
    <Modal title="Receive Return — condition triage" onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="return-dialog">
        <div className="col-span-3"><Field label="Product" required>
          <ProductPicker value={product} onPick={setProduct} testid="ret-product" /></Field></div>
        <Field label="Return type">
          <select value={f.return_type} onChange={(e) => setF({ ...f, return_type: e.target.value })}
              data-testid="ret-type" className={inputCls}>
            {["CUSTOMER_RETURN", "SUPPLIER_RETURN", "DEMO_RETURN", "LOANER_RETURN"].map((t) =>
              <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select></Field>
        <Field label="Warehouse" required>
          <select value={f.warehouse_id} onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
              data-testid="ret-warehouse" className={inputCls}>
            <option value="">—</option>
            {(meta?.warehouses || []).map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}
          </select></Field>
        <Field label="Customer / Source"><input value={f.customer_name}
            onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={inputCls} /></Field>
      </div>
      <div className="mt-3 space-y-2">
        <div className="text-[11px] font-semibold text-slate-700">Triage lines</div>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-3 gap-2" data-testid={`ret-line-${i}`}>
            <input value={l.serial} placeholder="Serial (blank = quantity line)"
                onChange={(e) => patch(i, { serial: e.target.value })}
                data-testid={`ret-line-${i}-serial`} className={inputCls + " font-mono"} />
            <input type="number" min={1} value={l.quantity} disabled={!!l.serial}
                onChange={(e) => patch(i, { quantity: e.target.value })} className={inputCls} />
            <select value={l.condition} onChange={(e) => patch(i, { condition: e.target.value })}
                data-testid={`ret-line-${i}-condition`} className={inputCls}>
              {(meta?.config?.conditions || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </div>))}
        <button onClick={() => setLines([...lines, { serial: "", quantity: 1, condition: "GOOD" }])}
            className="text-[10px] px-2 py-1 rounded border border-dashed border-slate-300">+ line</button>
        <div className="text-[10px] text-amber-700">DAMAGED / REPAIR REQUIRED / QUARANTINE conditions are kept OUT of available stock automatically.</div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="ret-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-amber-700 text-white">Receive &amp; Triage</button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
export function CustomerOwnedTab({ meta, canWrite }) {
  const [d, setD] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => {
    api.get("/inventory/customer-owned").then(({ data }) => setD(data));
    api.get("/inventory/customer-stock/receipts").then(({ data }) => setReceipts(data.items || []));
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="inv-customer-owned">
      <div className="flex items-center gap-2">
        {canWrite && (
          <button onClick={() => setOpen(true)} data-testid="csr-new"
              className="text-[11px] px-2.5 py-1.5 rounded-md bg-emerald-800 text-white">
            + Receive Customer Stock</button>)}
        <a href="/operations/employee-fulfilment"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-blue-700 text-blue-800"
            data-testid="co-goto-fulfilment">Employee Fulfilment →</a>
      </div>
      {receipts.length > 0 && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
            Customer Stock Receipts (CSR)</div>
          <table className="w-full text-[11px]"><tbody>
            {receipts.slice(0, 10).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`csr-row-${r.code}`}>
                <td className="px-3 py-1.5 font-mono text-[10px] font-semibold">{r.code}</td>
                <td className="px-3 py-1.5">{r.customer_name}</td>
                <td className="px-3 py-1.5">{r.product_name} × {r.quantity}</td>
                <td className="px-3 py-1.5">
                  <span className={"text-[8.5px] px-1.5 py-0.5 rounded " +
                      (r.match_status === "UNEXPECTED_RECEIPT_REVIEW" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>
                    {r.match_status.replace(/_/g, " ")}</span>
                  {r.apple_external && <span className="ml-1 text-[8.5px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">ABM: NOT IN SCOPE</span>}
                </td>
                <td className="px-3 py-1.5 text-[10px] text-slate-500">{fmtD(r.received_at)}</td>
              </tr>))}
          </tbody></table>
        </div>)}
      <div className="border border-slate-200 rounded-lg bg-white">
      <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
        Customer-owned stock — never counted as Suntronix sellable stock
      </div>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          <th className="text-left px-3 py-2">Customer</th><th className="text-left px-3 py-2">Product</th>
          <th className="text-left px-3 py-2">Serial</th><th className="text-left px-3 py-2">Status</th>
          <th className="text-left px-3 py-2">Age (days)</th></tr></thead>
        <tbody>
          {(!d || (!d.units?.length && !d.lots?.length)) &&
            <tr><td colSpan={5} className="text-center text-slate-400 px-3 py-6">No customer-owned stock held.</td></tr>}
          {(d?.units || []).map((u) => (
            <tr key={u.id} className="border-t border-slate-100" data-testid={`co-row-${u.serial}`}>
              <td className="px-3 py-2">{u.customer_name || "—"}</td>
              <td className="px-3 py-2">{u.product_name}</td>
              <td className="px-3 py-2 font-mono">{u.serial}</td>
              <td className="px-3 py-2 text-[10px]">{u.status}</td>
              <td className="px-3 py-2 font-mono">{u.age_days}</td></tr>))}
          {(d?.lots || []).map((l) => (
            <tr key={l.id} className="border-t border-slate-100">
              <td className="px-3 py-2">{l.customer_name || "—"}</td>
              <td className="px-3 py-2">{l.product_name}</td>
              <td className="px-3 py-2 font-mono text-slate-400">qty {l.on_hand}</td>
              <td className="px-3 py-2 text-[10px]">{l.condition}</td>
              <td className="px-3 py-2">—</td></tr>))}
        </tbody>
      </table>
    </div>
      {open && <ReceiveCustomerStockDialog meta={meta}
          onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

export function DemoLoanerTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/demo-loaner").then(({ data }) => setD(data)); }, []);
  return (
    <div className="space-y-2" data-testid="inv-demo-loaner">
      {d?.overdue_count > 0 && (
        <div className="border border-rose-300 bg-rose-50 text-rose-800 rounded-lg p-2.5 text-[11.5px]"
            data-testid="dl-overdue-banner">
          {d.overdue_count} demo/loaner unit(s) OVERDUE for return
        </div>)}
      <div className="border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Serial</th><th className="text-left px-3 py-2">Product</th>
            <th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Issued to</th>
            <th className="text-left px-3 py-2">Out (days)</th><th className="text-left px-3 py-2">Expected return</th></tr></thead>
          <tbody>
            {(!d || !d.items?.length) && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">
              Nothing on demo/loan. Issue via Stock Out with reason DEMO or LOANER.</td></tr>}
            {(d?.items || []).map((u) => (
              <tr key={u.id} className={"border-t border-slate-100 " + (u.overdue ? "bg-rose-50/60" : "")}
                  data-testid={`dl-row-${u.serial}`}>
                <td className="px-3 py-2 font-mono">{u.serial}</td>
                <td className="px-3 py-2">{u.product_name}</td>
                <td className="px-3 py-2 text-[10px]">{u.status}</td>
                <td className="px-3 py-2">{u.issued_to || u.out_destination || "—"}</td>
                <td className="px-3 py-2 font-mono">{u.out_days}</td>
                <td className="px-3 py-2 text-[10px]">{u.expected_return || "—"}
                  {u.overdue && <span className="ml-1 text-[8px] px-1 rounded bg-rose-200 text-rose-800">OVERDUE</span>}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RepairTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    Promise.all(["REPAIR", "QUARANTINE", "DAMAGED", "INSPECTION"].map((s) =>
      api.get("/inventory/units", { params: { status: s } }).then(({ data }) => data.items || [])))
      .then((all) => setItems(all.flat()));
  }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="inv-repair">
      <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
        Repair / Quarantine — non-sellable, excluded from Available
      </div>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          <th className="text-left px-3 py-2">Serial</th><th className="text-left px-3 py-2">Product</th>
          <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Condition</th>
          <th className="text-left px-3 py-2">Custody</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 px-3 py-6">Nothing in repair or quarantine.</td></tr>}
          {items.map((u) => (
            <tr key={u.id} className="border-t border-slate-100" data-testid={`rep-row-${u.serial}`}>
              <td className="px-3 py-2 font-mono">{u.serial}</td>
              <td className="px-3 py-2">{u.product_name}</td>
              <td className="px-3 py-2 text-[10px]">{u.status}</td>
              <td className="px-3 py-2 text-[10px]">{u.condition}</td>
              <td className="px-3 py-2 text-[10px]">{(u.custody || "").replace(/_/g, " ")}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}
